
import React from "react";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StatusBadge } from "./StatusBadge";
import { MentorApplication } from "./MentorTypes";

interface MentorsTableProps {
  applications: MentorApplication[];
  onViewDetails: (mentor: MentorApplication) => void;
  onUpdateStatus: (id: string, status: 'approved' | 'rejected') => Promise<void>;
}

const MentorsTable: React.FC<MentorsTableProps> = ({
  applications,
  onViewDetails,
  onUpdateStatus
}) => {
  return (
    <Table>
      <TableCaption>A list of mentor applications</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Position</TableHead>
          <TableHead>Experience</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {applications.map((application) => (
          <TableRow key={application.id}>
            <TableCell className="font-medium">
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={application.profile?.avatar_url} />
                  <AvatarFallback>
                    {application.profile?.full_name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                <span>{application.profile?.full_name}</span>
              </div>
            </TableCell>
            <TableCell>{application.position} at {application.company}</TableCell>
            <TableCell>{application.experience_years} years</TableCell>
            <TableCell><StatusBadge status={application.status} /></TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onViewDetails(application)}
                >
                  Details
                </Button>
                
                {application.status === 'pending' && (
                  <>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 text-green-500"
                            onClick={() => onUpdateStatus(application.id, 'approved')}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Approve</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => onUpdateStatus(application.id, 'rejected')}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Reject</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default MentorsTable;
